"use client";

import { Link } from "react-router-dom";
import { Text, Button, Badge, Section, SectionCard, CardTitle, CardDescription } from "@/src/components";
import {
  Palette,
  Component,
  Accessibility,
  Zap,
  Layers,
  BookOpen,
} from "lucide-react";

const features = [
  {
    icon: <Palette className="icon-lg" />,
    title: "Design Tokens",
    description:
      "Consistent colors, spacing, typography, and animation tokens for a unified design language.",
    href: "/docs/tokens/colors",
  },
  {
    icon: <Component className="icon-lg" />,
    title: "40+ Components",
    description:
      "Production-ready UI components and composite blocks for building any interface.",
    href: "/docs/components/button",
  },
  {
    icon: <Accessibility className="icon-lg" />,
    title: "Accessible",
    description:
      "WCAG 2.1 AA compliant with keyboard navigation, screen reader support, and focus management.",
    href: "/docs/accessibility",
  },
  {
    icon: <Zap className="icon-lg" />,
    title: "Fast & Light",
    description:
      "Optimized for performance with tree-shaking, lazy loading, and minimal bundle size.",
    href: "/docs/guidelines",
  },
  {
    icon: <Layers className="icon-lg" />,
    title: "Style Drivers",
    description:
      "Centralized styling system via componentStyle.ts for consistent, maintainable code.",
    href: "/docs/guidelines",
  },
  {
    icon: <BookOpen className="icon-lg" />,
    title: "Well Documented",
    description:
      "Comprehensive documentation with examples, guidelines, and best practices.",
    href: "/docs",
  },
];

const quickLinks = [
  { title: "Colors", href: "/docs/tokens/colors" },
  { title: "Typography", href: "/docs/tokens/typography" },
  { title: "Spacing", href: "/docs/tokens/spacing" },
  { title: "Button", href: "/docs/components/button" },
  { title: "Input", href: "/docs/components/input" },
  { title: "DataTable", href: "/docs/blocks/data-table" },
  { title: "Sheet", href: "/docs/components/sheet" },
  { title: "Sidebar", href: "/docs/blocks/sidebar" },
];

export default function DocsPage() {
  return (
    <>
      {/* Hero */}
      <Section>
        <div className="space-y-4">
          <div className="flex">
            <Text as="h1" className="text-h1 font-bold">
              Dream Light
            </Text>
            <sup>
              <Badge variant="secondary">v1.0.0</Badge>
            </sup>
          </div>
          <Text className="text-xl text-muted-foreground max-w-2xl">
            A comprehensive component library for building production-grade applications.
            Consistent, performant, accessible, and delightful to use.
          </Text>
          <div className="flex gap-3 pt-4">
            <Button asChild>
              <Link to="/docs/installation">Get Started</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/">View Showcase</Link>
            </Button>
          </div>
        </div>
      </Section>

      {/* Features Grid */}
      <Section>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((feature) => (
            <Link key={feature.title} to={feature.href}>
              <SectionCard
                contentClassName="p-6 h-full hover:bg-accent/30 transition-colors cursor-pointer"
              >
                <div className="space-y-3">
                  <div className="text-primary">{feature.icon}</div>
                  <Text className="font-semibold">{feature.title}</Text>
                  <Text className="text-sm text-muted-foreground">
                    {feature.description}
                  </Text>
                </div>
              </SectionCard>
            </Link>
          ))}
        </div>
      </Section>

      {/* Quick Links */}
      <Section>
        <SectionCard
          header={
            <>
              <CardTitle>Quick Links</CardTitle>
              <CardDescription>Jump to commonly used sections</CardDescription>
            </>
          }
          contentClassName="p-6"
        >
          <div className="flex flex-wrap gap-2">
            {quickLinks.map((link) => (
              <Button key={link.href} variant="outline" size="sm" asChild>
                <Link to={link.href}>{link.title}</Link>
              </Button>
            ))}
          </div>
        </SectionCard>
      </Section>

      {/* Core Principles */}
      <Section>
        <div className="space-y-4">
          <Text as="h2" className="text-h3 font-bold">
            Core Principles
          </Text>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Text className="font-semibold">Single Source of Truth</Text>
              <Text className="text-sm text-muted-foreground">
                All styles come from componentStyle.ts. No scattered inline Tailwind for
                structural styling.
              </Text>
            </div>
            <div className="space-y-2">
              <Text className="font-semibold">Composition Over Complexity</Text>
              <Text className="text-sm text-muted-foreground">
                Components use slot patterns for maximum flexibility without prop
                explosion.
              </Text>
            </div>
            <div className="space-y-2">
              <Text className="font-semibold">Accessible by Default</Text>
              <Text className="text-sm text-muted-foreground">
                Keyboard navigation, ARIA attributes, and screen reader support built-in.
              </Text>
            </div>
            <div className="space-y-2">
              <Text className="font-semibold">Dark Mode Ready</Text>
              <Text className="text-sm text-muted-foreground">
                Full dark mode support with semantic color tokens that adapt automatically.
              </Text>
            </div>
          </div>
        </div>
      </Section>

      {/* Installation Preview */}
      <Section>
        <div className="space-y-4">
          <Text as="h2" className="text-h3 font-bold">
            Quick Installation
          </Text>
          <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm">
            <code className="text-foreground">
              npx shadcn@latest add @bluecopa-ui/button
            </code>
          </div>
          <Text className="text-sm text-muted-foreground">
            Or copy components directly from the registry folder.{" "}
            <Link to="/docs/installation" className="text-primary hover:underline">
              Learn more
            </Link>
          </Text>
        </div>
      </Section>
    </>
  );
}
